import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { PageHeader } from '@/shared/components/PageHeader';
import { Input } from '@/shared/components/Input';
import { ArticleItem } from '../components/ArticleItem';
import { normalize } from '../utils/normalize';
const PAGE_SIZE = 50;
/**
 * Generic viewer for an articulated legal text (Código Penal, Constitución):
 * accent-insensitive search over number + hierarchy + body, chips filtering
 * by libro or título, hierarchy separators and collapsible articles. Shared by
 * every role; the data comes from @academia-apolo/shared-content.
 */
export function LawCodePage({ data, filterLevel, searchPlaceholder }) {
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [visible, setVisible] = useState(PAGE_SIZE);
    const chips = useMemo(() => buildChips(data, filterLevel), [data, filterLevel]);
    const filtered = useMemo(() => filterArticles(data.articles, query, filter, filterLevel), [data, query, filter, filterLevel]);
    // Reset pagination on filter change
    useMemoReset(() => setVisible(PAGE_SIZE), [filtered.length]);
    const shown = filtered.slice(0, visible);
    const hasMore = filtered.length > visible;
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: data.meta.title, description: data.meta.subtitle }), _jsx("div", { className: "mb-4", children: _jsx(Input, { type: "search", placeholder: searchPlaceholder, value: query, onChange: (e) => setQuery(e.target.value) }) }), _jsx("div", { className: "mb-4 flex flex-wrap gap-2", children: chips.map((c) => (_jsx(FilterButton, { active: filter === c.value, onClick: () => setFilter(c.value), title: c.title, children: c.label }, c.value))) }), _jsxs("div", { className: "mb-3 text-xs text-gray-500 dark:text-gray-400", children: [filtered.length, " art\u00EDculo", filtered.length === 1 ? '' : 's', filtered.length !== data.articles.length && ` (de ${data.articles.length})`] }), filtered.length === 0 ? (_jsxs("p", { className: "rounded-lg border p-8 text-center text-sm text-gray-500 dark:text-gray-400", style: { borderColor: 'var(--border)' }, children: ["Sin resultados para \u00AB", query, "\u00BB."] })) : (_jsxs("div", { className: "space-y-2.5", children: [renderWithSeparators(shown, query, filtered.length), hasMore && (_jsx("div", { className: "pt-3 text-center", children: _jsxs("button", { type: "button", onClick: () => setVisible((v) => v + PAGE_SIZE), className: "rounded-lg border px-4 py-2 text-sm font-semibold transition-colors hover:bg-[var(--hover)]", style: { borderColor: 'var(--border)', color: 'var(--accent)' }, children: ["Mostrar 50 m\u00E1s (", filtered.length - visible, " restantes)"] }) }))] }))] }));
}
function FilterButton({ active, onClick, children, title, }) {
    return (_jsx("button", { type: "button", onClick: onClick, title: title, className: "rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors", style: {
            background: active ? 'rgba(var(--accent-rgb), 0.15)' : 'var(--surface)',
            color: active ? 'var(--accent)' : 'var(--fg-2)',
            border: '1px solid var(--border)',
        }, children: children }));
}
/* ── Chips ─────────────────────────────────────────────────────────────── */
const DISPOSICIONES_RE = /^DISPOSICI/i;
function buildChips(data, level) {
    const chips = [{ value: 'all', label: 'Todo' }];
    if (level === 'libro') {
        for (const l of data.hierarchyOptions.libros)
            chips.push({ value: l, label: libroShort(l), title: l });
        chips.push({ value: 'preliminar', label: 'Título preliminar' });
        return chips;
    }
    let hasDisposiciones = false;
    for (const t of data.hierarchyOptions.titulos) {
        if (DISPOSICIONES_RE.test(t)) {
            hasDisposiciones = true;
            continue;
        }
        chips.push({ value: t, label: tituloShort(t), title: t });
    }
    // The adicionales/transitorias/derogatoria/final blocks collapse into one chip.
    if (hasDisposiciones)
        chips.push({ value: 'disposiciones', label: 'Disposiciones' });
    return chips;
}
function libroShort(libro) {
    const m = libro.match(/^LIBRO\s+([IVX]+)/i);
    return m ? `Libro ${m[1]}` : libro.slice(0, 30);
}
function tituloShort(titulo) {
    if (/^T[ÍI]TULO\s+PRELIMINAR/i.test(titulo))
        return 'Preliminar';
    const m = titulo.match(/^T[ÍI]TULO\s+([IVX]+)/i);
    if (m)
        return `Título ${m[1]}`;
    return sentenceCase(titulo);
}
/** Sentence case that keeps a trailing roman numeral uppercase ("TÍTULO VI" → "Título VI"). */
function sentenceCase(s) {
    return (s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).replace(/\b([ivx]+)$/i, (numeral) => numeral.toUpperCase());
}
/* ── Filtering ─────────────────────────────────────────────────────────── */
function filterArticles(all, query, filter, level) {
    const q = normalize(query);
    const tokens = q.split(' ').filter(Boolean);
    const matchesFilter = (a) => {
        if (filter === 'all')
            return true;
        if (level === 'libro') {
            if (filter === 'preliminar')
                return !a.hierarchy.libro && /PRELIMINAR/.test(a.hierarchy.titulo);
            return a.hierarchy.libro === filter;
        }
        if (filter === 'disposiciones')
            return DISPOSICIONES_RE.test(a.hierarchy.titulo);
        return a.hierarchy.titulo === filter;
    };
    const matchesQuery = (article) => {
        if (!tokens.length)
            return true;
        // Allow searching by number directly: a token like "138" matches the article number.
        const haystack = normalize([
            `art ${article.number}`,
            `artículo ${article.number}`,
            article.label ?? '',
            article.hierarchy.libro,
            article.hierarchy.titulo,
            article.hierarchy.capitulo,
            article.hierarchy.seccion,
            ...article.body,
            ...article.notes,
        ].join(' '));
        return tokens.every((t) => haystack.includes(t));
    };
    return all.filter((a) => matchesFilter(a) && matchesQuery(a));
}
// Run a side-effect when the dependency array changes (without using useEffect's
// async-after-paint scheduling). Keeps pagination reset in sync with filtering.
function useMemoReset(fn, deps) {
    useMemo(() => {
        fn();
    }, deps);
}
/* ── Hierarchy separators ──────────────────────────────────────────────── */
// Renders the article list interleaved with hierarchy separators (libro / título /
// capítulo / sección) — each header appears once when its level transitions. Headers
// also emit when the previous article had a less-deep hierarchy (e.g. the very first
// article still gets its title shown).
function renderWithSeparators(articles, query, totalFiltered) {
    const out = [];
    let prev = null;
    for (const a of articles) {
        const h = a.hierarchy;
        if (!prev || prev.libro !== h.libro)
            pushHeader(out, 'libro', h.libro);
        if (!prev || prev.titulo !== h.titulo)
            pushHeader(out, 'titulo', h.titulo);
        if (!prev || prev.capitulo !== h.capitulo)
            pushHeader(out, 'capitulo', h.capitulo);
        if (!prev || prev.seccion !== h.seccion)
            pushHeader(out, 'seccion', h.seccion);
        out.push(_jsx(ArticleItem, { article: a, query: query, defaultOpen: query.trim().length > 0 && totalFiltered <= 8 }, `art-${a.number}-${a.hierarchy.titulo}`));
        prev = h;
    }
    return out;
}
function pushHeader(arr, level, raw) {
    if (!raw)
        return;
    arr.push(_jsx(HierarchyHeader, { level: level, text: raw }, `hdr-${level}-${arr.length}`));
}
function HierarchyHeader({ level, text }) {
    const styles = {
        libro: { wrapper: 'mt-6 first:mt-0', eyebrow: 'Libro', size: 'text-base font-bold', pt: 'pt-2' },
        titulo: { wrapper: 'mt-5 first:mt-2', eyebrow: 'Título', size: 'text-sm font-semibold', pt: 'pt-1.5' },
        capitulo: { wrapper: 'mt-4 first:mt-1', eyebrow: 'Capítulo', size: 'text-sm font-semibold', pt: 'pt-1' },
        seccion: { wrapper: 'mt-3 first:mt-0', eyebrow: 'Sección', size: 'text-xs font-medium', pt: '' },
    };
    const cfg = styles[level];
    const { head, rest } = splitHeader(text);
    return (_jsx("div", { className: `${cfg.wrapper} ${cfg.pt}`, children: _jsxs("div", { className: "flex items-baseline gap-2 border-b pb-1.5", style: { borderColor: 'var(--border)' }, children: [_jsx("span", { className: "text-[10px] font-bold uppercase tracking-[0.18em]", style: { color: 'var(--accent)' }, children: head || cfg.eyebrow }), rest && (_jsx("span", { className: `${cfg.size} text-gray-700 dark:text-gray-200`, children: rest }))] }) }));
}
/**
 * "TÍTULO I De la infracción..." → { head: "Título I", rest: "De la infracción..." }.
 * Headers without a numbered prefix (PREÁMBULO, DISPOSICIONES ADICIONALES…) become
 * the head themselves, in sentence case.
 */
function splitHeader(s) {
    const m = s.match(/^(LIBRO\s+[IVX]+|T[ÍI]TULO\s+(?:PRELIMINAR|[IVX]+)|CAP[ÍI]TULO\s+(?:[IVX]+|PRIMERO|SEGUNDO|TERCERO|CUARTO|QUINTO|SEXTO)|Secci[óo]n\s+\d+\.?[ºª]?)\s*(.*)$/i);
    if (!m || !m[1])
        return { head: sentenceCase(s), rest: '' };
    return { head: sentenceCase(m[1]), rest: (m[2] ?? '').trim() };
}
//# sourceMappingURL=LawCodePage.js.map